import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import {
  apiErrorData,
  extractApiExceptionCodeFromData,
} from "@/core/lib/api-errors";
import type { InferHeyApiMutationOptions } from "@/core/lib/types";
import { verifyTwoFaSigninMutation } from "@/openapi/@tanstack/react-query.gen";

import { useTwoFaCompletion } from "./use-two-fa-completion";

export const useTwoFaVerifySignIn = (
  options?: InferHeyApiMutationOptions<typeof verifyTwoFaSigninMutation>,
) => {
  const navigate = useNavigate();

  const completion = useTwoFaCompletion();
  const mutation = useMutation({
    ...verifyTwoFaSigninMutation(),
    ...options,
    gcTime: 0,
    retry: false,
    onSuccess: completion.complete,
    onError: (error, variables, onMutateResult, context) => {
      const code = extractApiExceptionCodeFromData(apiErrorData(error) ?? null);
      // navigate rather than `throw redirect(...)`: this callback is not on the
      // promise chain `mutateAsync` returns, so a thrown redirect escapes as an
      // unhandled rejection: the router never performs it and the early return
      // below never happens either, leaving the form with no redirect and no error
      if (code === "TWO_FA_NEEDS_REGENERATION") {
        navigate({ to: "/auth/two-fa", search: { regenerate: true } });
        return;
      }
      if (
        code === "TWO_FA_NOT_ENABLED" ||
        code === "TWO_FA_NOT_CONFIGURED" ||
        code === "TWO_FA_SETUP_INCOMPLETE"
      ) {
        navigate({ to: "/auth/two-fa" });
        return;
      }
      options?.onError?.(error, variables, onMutateResult, context);
    },
  });

  return {
    ...mutation,
    recoveryCodes: completion.recoveryCodes,
    continueAfterSaving: () => {
      mutation.reset();
      return completion.continueAfterSaving();
    },
  };
};
